import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { NAVIGATION_CONFIG, NavSection, NavItem } from '../../config/navigation';

export interface ActiveSectionResult {
  sectionId: string | null;
  itemId: string | null;
  section: NavSection | null;
  item: NavItem | null;
}

/**
 * Hook to detect which navigation section and item is currently active
 * based on the URL pathname.
 *
 * @returns ActiveSectionResult containing the active section and item IDs and objects
 *
 * @example
 * ```typescript
 * const { sectionId, itemId, section, item } = useActiveSection();
 * // On /przewodnik/taryfy:
 * // sectionId: 'przewodnik', itemId: 'taryfy'
 * // section: { id: 'przewodnik', ... }, item: { id: 'taryfy', ... }
 * ```
 */
export function useActiveSection(): ActiveSectionResult {
  const location = useLocation();

  return useMemo(() => {
    const pathname = location.pathname;

    // Find matching section by checking if pathname starts with section's basePath
    const activeSection = NAVIGATION_CONFIG.find((section) => {
      // Exact match for root paths (e.g., /aktualnosci)
      if (pathname === section.basePath) {
        return true;
      }

      // Prefix match for nested paths (e.g., /przewodnik/taryfy, /aktualnosci/:slug)
      if (pathname.startsWith(section.basePath + '/')) {
        return true;
      }

      return false;
    });

    if (!activeSection) {
      return {
        sectionId: null,
        itemId: null,
        section: null,
        item: null,
      };
    }

    // Find matching item within section
    // Try exact match first, then prefix match
    let activeItem: NavItem | null = null;

    // Exact match
    activeItem = activeSection.items.find((item) => item.path === pathname) || null;

    // If no exact match, try prefix match for nested routes
    // Example: /przewodnik/analizy/:slug should match /przewodnik/analizy
    if (!activeItem) {
      activeItem = activeSection.items.find((item) => {
        // Check if current path starts with item path followed by /
        if (pathname.startsWith(item.path + '/')) {
          return true;
        }
        return false;
      }) || null;
    }

    return {
      sectionId: activeSection.id,
      itemId: activeItem?.id || null,
      section: activeSection,
      item: activeItem,
    };
  }, [location.pathname]);
}
